import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { DollarSign, AlertTriangle, CheckCircle, Clock, Trash2, RefreshCw, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

const inputCls = 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-nova-500 focus:border-nova-500 outline-none text-sm';
const fmt = (n) => '$' + (n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function StatusPill({ status }) {
  if (status === 'verified') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="w-3 h-3" /> Verified
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
      <Clock className="w-3 h-3" /> Awaiting Proof
    </span>
  );
}

// Compute billing dates from start_date + term_months
function billingDates(startDate, termMonths, monthlyAmount) {
  if (!startDate || !termMonths) return [];
  const [y, m, d] = startDate.split('-').map(Number);
  const dates = [];
  for (let i = 0; i < termMonths; i++) {
    const dt = new Date(y, m - 1 + i, d);
    const label = dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
    const iso = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
    dates.push({ label, iso, amount: monthlyAmount });
  }
  return dates;
}

function PaymentForm({ sale, onSave, onCancel, submitting }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ date: today, amount: '', notes: '', payment_method: '' });
  const set = f => setForm(p => ({ ...p, ...f }));
  return (
    <div className="max-w-xl">
      <p className="text-sm font-medium text-gray-700 mb-3">
        Record Payment — {sale.customer_name}
        {sale.payment_type === 'financed' && (
          <span className="ml-2 text-xs text-purple-600 font-normal">Monthly will recalculate after saving</span>
        )}
      </p>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date</label>
          <input type="date" value={form.date} onChange={e => set({ date: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Amount</label>
          <input type="number" step="0.01" value={form.amount} onChange={e => set({ amount: e.target.value })}
            className={inputCls} placeholder="0.00" />
          <p className="text-xs text-gray-400 mt-0.5">Balance: {fmt(sale.remaining)}</p>
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Payment Method <span className="text-red-500">*</span></label>
          <select value={form.payment_method} onChange={e => set({ payment_method: e.target.value })} className={inputCls}>
            <option value="">Select method…</option>
            <option value="cash">Cash</option>
            <option value="etransfer">E-Transfer</option>
            <option value="cc">Credit Card</option>
            <option value="cheque">Cheque</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Notes</label>
          <input type="text" value={form.notes} onChange={e => set({ notes: e.target.value })}
            className={inputCls} placeholder="e.g. paid on install day, deposit reduction…" />
        </div>
      </div>
      <div className="flex gap-2">
        <button disabled={submitting || !form.amount || !form.payment_method}
          onClick={() => onSave(form)}
          className="px-4 py-2 text-sm font-medium bg-nova-600 text-white rounded-lg hover:bg-nova-700 disabled:opacity-50">
          {submitting ? 'Saving...' : 'Save Payment'}
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-sm font-medium bg-white border text-gray-600 rounded-lg hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </div>
  );
}

function RotessaSetupForm({ sale, onSave, onCancel, submitting }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ start_date: today, rotessa_link: '', notes: '' });
  const set = f => setForm(p => ({ ...p, ...f }));
  return (
    <div className="max-w-xl">
      <p className="text-sm font-semibold text-purple-800 mb-1">Setup Recurring Billing — {sale.customer_name}</p>
      <p className="text-xs text-purple-600 mb-3">
        Confirm recurring billing is active on Rotessa. This only needs to be done once.
      </p>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">First Billing Date</label>
          <input type="date" value={form.start_date} onChange={e => set({ start_date: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Monthly Amount</label>
          <input className={`${inputCls} bg-gray-50`} value={`${fmt(sale.finance_monthly_amount)} / mo × ${sale.finance_term_months} mo`} readOnly />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-gray-500 mb-1">
            Rotessa Payment Schedule Link
            <span className="text-gray-400 font-normal ml-1">(paste the link from Rotessa — opens in new tab)</span>
          </label>
          <input type="url" value={form.rotessa_link} onChange={e => set({ rotessa_link: e.target.value })}
            className={inputCls} placeholder="https://app.rotessa.com/…" />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Notes</label>
          <input type="text" value={form.notes} onChange={e => set({ notes: e.target.value })}
            className={inputCls} placeholder="e.g. PAD set up under client email, CC recurring on Stripe…" />
        </div>
      </div>
      <div className="flex gap-2">
        <button disabled={submitting || !form.start_date}
          onClick={() => onSave(form)}
          className="px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
          {submitting ? 'Saving...' : 'Confirm Billing Setup'}
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-sm font-medium bg-white border text-gray-600 rounded-lg hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </div>
  );
}

function CollectionsTab({ highlightId }) {
  const [outstanding, setOutstanding] = useState([]);
  const [expanded, setExpanded] = useState(null); // { id, type: 'payment' | 'rotessa' | 'cash' }
  const [submitting, setSubmitting] = useState(false);
  const highlightRef = useRef(null);

  const load = () => api.get('/payments/outstanding').then(setOutstanding);
  useEffect(() => { load(); }, []);

  // Scroll to highlighted row after data loads
  useEffect(() => {
    if (highlightId && outstanding.length > 0 && highlightRef.current) {
      setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    }
  }, [highlightId, outstanding]);

  const open = (id, type) => setExpanded(expanded?.id === id && expanded?.type === type ? null : { id, type });

  const handlePayment = async (sale, form) => {
    setSubmitting(true);
    try {
      await api.post('/payments', {
        sale_id: sale.id, amount: Number(form.amount),
        payment_date: form.date, payment_method: form.payment_method, notes: form.notes || '',
      });
      setExpanded(null);
      load();
    } catch (e) { alert(e.message); } finally { setSubmitting(false); }
  };

  const handleRotessaSetup = async (sale, form) => {
    setSubmitting(true);
    try {
      await api.post('/payments/financed-setup', {
        sale_id: sale.id, start_date: form.start_date,
        rotessa_link: form.rotessa_link || null, notes: form.notes || '',
      });
      setExpanded(null);
      load();
    } catch (e) { alert(e.message); } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Customer</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Deal Total</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Paid</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Remaining</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Type</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Last Payment</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {outstanding.map(sale => {
            const isHighlighted = sale.id === highlightId;
            return (
            <React.Fragment key={sale.id}>
              <tr
                ref={isHighlighted ? highlightRef : null}
                className={`hover:bg-gray-50 transition-colors ${isHighlighted ? 'bg-amber-50 ring-2 ring-inset ring-amber-400' : ''}`}
              >
                <td className="px-4 py-3">
                  <Link to={`/sales/${sale.id}`} className="text-nova-600 hover:underline font-medium">{sale.customer_name}</Link>
                  <p className="text-xs text-gray-400">{sale.city}</p>
                </td>
                <td className="px-4 py-3">{fmt(sale.deal_total)}</td>
                <td className="px-4 py-3 text-green-600">{fmt(sale.total_paid)}</td>
                <td className="px-4 py-3 text-red-500 font-bold">{fmt(sale.remaining)}</td>
                <td className="px-4 py-3">
                  <span className="capitalize">{sale.payment_type}</span>
                  {sale.payment_type === 'financed' && sale.finance_monthly_amount && (
                    <p className="text-xs text-gray-400">{fmt(sale.finance_monthly_amount)}/mo × {sale.finance_term_months}mo</p>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-400">{sale.last_payment_date || 'Never'}</td>
                <td className="px-4 py-3">
                  {sale.payment_type === 'financed' ? (
                    <div className="flex flex-col gap-1">
                      <button onClick={() => open(sale.id, 'payment')}
                        className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 whitespace-nowrap">
                        Record Payment
                      </button>
                      <button onClick={() => open(sale.id, 'rotessa')}
                        className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 whitespace-nowrap">
                        Setup Rotessa / CC
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => open(sale.id, 'cash')}
                      className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700">
                      Mark Received
                    </button>
                  )}
                </td>
              </tr>

              {/* Inline forms */}
              {expanded?.id === sale.id && (
                <tr key={`form-${sale.id}`} className={expanded.type === 'rotessa' ? 'bg-purple-50' : 'bg-green-50'}>
                  <td colSpan={7} className="px-6 py-4">
                    {(expanded.type === 'payment' || expanded.type === 'cash') && (
                      <PaymentForm sale={sale} submitting={submitting}
                        onSave={(form) => handlePayment(sale, form)}
                        onCancel={() => setExpanded(null)} />
                    )}
                    {expanded.type === 'rotessa' && (
                      <RotessaSetupForm sale={sale} submitting={submitting}
                        onSave={(form) => handleRotessaSetup(sale, form)}
                        onCancel={() => setExpanded(null)} />
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
            );
          })}
          {outstanding.length === 0 && (
            <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No outstanding balances</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RecurringTab({ highlightId }) {
  const [recurring, setRecurring] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [paymentForm, setPaymentForm] = useState(null); // sale id with open payment form
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const highlightRef = useRef(null);

  const load = () => api.get('/payments/recurring').then(setRecurring);
  useEffect(() => { load(); }, []);

  // Auto-expand and scroll to highlighted sale
  useEffect(() => {
    if (highlightId && recurring.length > 0) {
      const match = recurring.find(s => s.id === highlightId);
      if (match) {
        setExpanded(highlightId);
        setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
      }
    }
  }, [highlightId, recurring]);

  const openPaymentForm = (saleId, remaining) => {
    setPaymentForm(saleId);
    setForm({ amount: remaining > 0 ? remaining.toFixed(2) : '', date: today, payment_method: '', notes: '' });
  };

  const handleRecordPayment = async (sale) => {
    if (!form.payment_method) { alert('Please select a payment method.'); return; }
    setSubmitting(true);
    try {
      await api.post('/payments', {
        sale_id: sale.id,
        amount: Number(form.amount),
        payment_date: form.date,
        payment_method: form.payment_method,
        notes: form.notes || '',
      });
      setPaymentForm(null);
      load();
    } catch (e) { alert(e.message); } finally { setSubmitting(false); }
  };

  const handleRemoveSetup = async (sale) => {
    if (!window.confirm(`Remove Rotessa/CC setup for ${sale.customer_name}? This clears the billing start date. Already-recorded payments are kept.`)) return;
    await api.delete(`/payments/rotessa-setup/${sale.id}`);
    load();
  };

  const activeRecurring = recurring.filter(s => (s.remaining || 0) > 0);
  const monthlyRevenue   = activeRecurring.reduce((sum, s) => sum + (s.finance_monthly_amount || 0), 0);
  const totalContracted  = recurring.reduce((sum, s) => sum + (s.finance_monthly_amount || 0) * (s.finance_term_months || 0), 0);
  const totalCollected   = recurring.reduce((sum, s) => sum + (s.total_paid || 0), 0);
  const accountsReceivable = activeRecurring.reduce((sum, s) => sum + Math.max(0, s.remaining || 0), 0);

  return (
    <div>
      {/* AR Summary */}
      <div className="bg-white rounded-xl border p-5 mb-4">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Accounts Receivable — Financed Deals</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-400 mb-1">Active Contracts</p>
            <p className="text-2xl font-bold text-purple-600">{activeRecurring.length}</p>
            <p className="text-xs text-gray-400 mt-0.5">{recurring.length} total</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Accounts Receivable</p>
            <p className="text-2xl font-bold text-blue-600">{fmt(accountsReceivable)}</p>
            <p className="text-xs text-gray-400 mt-0.5">outstanding balance</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Collected to Date</p>
            <p className="text-2xl font-bold text-green-600">{fmt(totalCollected)}</p>
            <p className="text-xs text-gray-400 mt-0.5">of {fmt(totalContracted)} contracted</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Monthly Revenue</p>
            <p className="text-2xl font-bold text-nova-600">{fmt(monthlyRevenue)}</p>
            <p className="text-xs text-gray-400 mt-0.5">expected this month</p>
          </div>
        </div>

        {/* AR progress bar */}
        {totalContracted > 0 && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Collected {totalContracted > 0 ? ((totalCollected / totalContracted) * 100).toFixed(0) : 0}%</span>
              <span>Remaining {totalContracted > 0 ? ((accountsReceivable / totalContracted) * 100).toFixed(0) : 0}%</span>
            </div>
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${Math.min(100, (totalCollected / totalContracted) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {recurring.map(sale => {
          const dates = billingDates(sale.finance_start_date, sale.finance_term_months, sale.finance_monthly_amount);
          const billedSet = new Set(sale.billed_dates || []);
          const unbilledDates = dates.filter(d => !billedSet.has(d.iso));
          const isPaidOff = (sale.remaining || 0) <= 0;
          const isActive = !!sale.finance_start_date && !isPaidOff; // billing set up, still running
          const isOpen = expanded === sale.id;
          const isHighlighted = sale.id === highlightId;

          return (
            <div
              key={sale.id}
              ref={isHighlighted ? highlightRef : null}
              className={`bg-white rounded-xl border overflow-hidden ${isPaidOff ? 'opacity-60' : ''} ${isHighlighted ? 'ring-2 ring-amber-400' : ''}`}
            >
              <button className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 text-left"
                onClick={() => setExpanded(isOpen ? null : sale.id)}>
                <div className="flex items-center gap-4">
                  {/* Status icon */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isPaidOff ? 'bg-green-100' : isActive ? 'bg-purple-100' : 'bg-orange-100'
                  }`}>
                    {isPaidOff
                      ? <CheckCircle className="w-5 h-5 text-green-600" />
                      : isActive
                        ? <RefreshCw className="w-5 h-5 text-purple-600" />
                        : <AlertTriangle className="w-5 h-5 text-orange-500" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/sales/${sale.id}`} className="font-semibold text-nova-600 hover:underline" onClick={e => e.stopPropagation()}>
                        {sale.customer_name}
                      </Link>
                      {isPaidOff && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Paid Off</span>
                      )}
                      {isActive && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-0.5">
                          <RefreshCw className="w-2.5 h-2.5" /> Billing Active
                        </span>
                      )}
                      {!isActive && !isPaidOff && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">⚠ Setup Needed</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{sale.city} · {sale.salesman_name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-5 text-right flex-wrap justify-end">
                  <div>
                    <p className="text-xs text-gray-400">Monthly</p>
                    <p className="font-bold text-purple-600">{fmt(sale.finance_monthly_amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Collected</p>
                    <p className="font-semibold text-green-600">{fmt(sale.total_paid)}</p>
                  </div>
                  {!isPaidOff && (
                    <div>
                      <p className="text-xs text-gray-400">{isActive ? 'Auto-collecting' : 'Remaining'}</p>
                      <p className={`font-bold ${isActive ? 'text-purple-600' : 'text-orange-500'}`}>
                        {fmt(sale.remaining)}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-400">Payments left</p>
                    <p className="font-semibold text-gray-700">{isPaidOff ? '—' : `${unbilledDates.length} of ${dates.length}`}</p>
                  </div>
                  <span className="text-gray-400 text-lg">{isOpen ? '▲' : '▼'}</span>
                </div>
              </button>

              {isOpen && (
                <div className="border-t px-5 py-4">
                  {/* Status banner */}
                  {isActive && (
                    <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-purple-50 border border-purple-200 rounded-xl">
                      <RefreshCw className="w-4 h-4 text-purple-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-purple-800">Rotessa / CC billing is active</p>
                        <p className="text-xs text-purple-600">
                          Auto-billing {fmt(sale.finance_monthly_amount)}/mo — {unbilledDates.length} payment{unbilledDates.length !== 1 ? 's' : ''} remaining. No action needed.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {sale.rotessa_link && (
                          <a href={sale.rotessa_link} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-purple-600 hover:text-purple-800 text-xs font-medium border border-purple-300 rounded-lg px-2 py-1 hover:bg-purple-100">
                            <ExternalLink className="w-3 h-3" /> Rotessa
                          </a>
                        )}
                        <button onClick={() => handleRemoveSetup(sale)}
                          title="Remove Rotessa setup"
                          className="flex items-center gap-1 text-red-400 hover:text-red-600 text-xs font-medium border border-red-200 rounded-lg px-2 py-1 hover:bg-red-50">
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    </div>
                  )}
                  {!isActive && !isPaidOff && (
                    <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl">
                      <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-orange-800">Rotessa / CC not set up yet</p>
                        <p className="text-xs text-orange-600">Recurring billing hasn't been activated. Set it up to start auto-collecting.</p>
                      </div>
                    </div>
                  )}

                  {/* Meta info */}
                  <div className="flex flex-wrap gap-4 mb-4 text-sm">
                    {sale.finance_start_date && <div><p className="text-xs text-gray-400">First billing</p><p className="font-medium">{sale.finance_start_date}</p></div>}
                    <div><p className="text-xs text-gray-400">Term</p><p className="font-medium">{sale.finance_term_months} months</p></div>
                    <div><p className="text-xs text-gray-400">Total collected</p><p className="font-medium text-green-600">{fmt(sale.total_paid)}</p></div>
                    <div>
                      <p className="text-xs text-gray-400">{isActive ? 'Auto-collecting' : 'Balance'}</p>
                      <p className={`font-medium ${isPaidOff ? 'text-green-600' : isActive ? 'text-purple-600' : 'text-orange-500'}`}>
                        {isPaidOff ? 'Paid off' : fmt(sale.remaining)}
                      </p>
                    </div>
                    {sale.finance_cc_fee_pct > 0 && (
                      <div><p className="text-xs text-gray-400">CC fee</p><p className="font-medium">{sale.finance_cc_fee_pct}%</p></div>
                    )}
                    {sale.finance_notes && (
                      <div><p className="text-xs text-gray-400">Notes</p><p className="text-gray-600">{sale.finance_notes}</p></div>
                    )}
                  </div>

                  {/* Record one-time / payoff payment */}
                  {!isPaidOff && (
                    <div className="mb-4">
                      {paymentForm !== sale.id ? (
                        <div className="flex gap-2">
                          <button onClick={() => openPaymentForm(sale.id, sale.remaining)}
                            className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1">
                            <DollarSign className="w-3 h-3" /> Record Payment
                          </button>
                          <button onClick={() => openPaymentForm(sale.id, sale.remaining)}
                            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Pay Off Balance ({fmt(sale.remaining)})
                          </button>
                        </div>
                      ) : (
                        <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                          <p className="text-sm font-medium text-gray-700 mb-3">Record Payment — {sale.customer_name}</p>
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Amount</label>
                              <input type="number" step="0.01" value={form.amount}
                                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                                className={inputCls} placeholder="0.00" />
                              <p className="text-xs text-gray-400 mt-0.5">Balance: {fmt(sale.remaining)}</p>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Date</label>
                              <input type="date" value={form.date}
                                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                                className={inputCls} />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">Payment Method <span className="text-red-500">*</span></label>
                              <select value={form.payment_method}
                                onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}
                                className={inputCls}>
                                <option value="">Select method…</option>
                                <option value="cash">Cash</option>
                                <option value="etransfer">E-Transfer</option>
                                <option value="cc">Credit Card</option>
                                <option value="cheque">Cheque</option>
                              </select>
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">Notes</label>
                              <input type="text" value={form.notes}
                                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                                className={inputCls} placeholder="e.g. paid on install day, early payoff, etc." />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button disabled={submitting || !form.amount || !form.payment_method}
                              onClick={() => handleRecordPayment(sale)}
                              className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                              {submitting ? 'Saving...' : 'Save Payment'}
                            </button>
                            <button onClick={() => setPaymentForm(null)}
                              className="px-4 py-2 text-sm font-medium bg-white border text-gray-600 rounded-lg hover:bg-gray-50">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Billing schedule */}
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Billing Schedule</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {dates.map((d, i) => {
                      const billedSet = new Set(sale.billed_dates || []);
                      const isBilled = billedSet.has(d.iso);
                      const isToday = d.iso === today;
                      const isNext = !isBilled && !isPaidOff && unbilledDates[0]?.iso === d.iso;
                      return (
                        <div key={i} className={`rounded-lg px-3 py-2 text-center text-xs border ${
                          isToday && isBilled ? 'bg-green-100 border-green-400 text-green-800' :
                          isBilled || isPaidOff ? 'bg-gray-50 border-gray-200 text-gray-400' :
                          isNext ? 'bg-blue-50 border-blue-300 text-blue-700' :
                          'bg-white border-gray-200 text-gray-600'
                        }`}>
                          <p className={`font-medium ${(isBilled || isPaidOff) ? 'line-through' : ''}`}>{d.label}</p>
                          <p className={isBilled || isPaidOff ? 'text-gray-400' : isNext ? 'text-blue-600 font-medium' : 'text-gray-600'}>{fmt(d.amount)}</p>
                          {isBilled && isToday && <p className="text-[10px] text-green-600 font-semibold">Billed today</p>}
                          {isBilled && !isToday && <p className="text-[10px] text-gray-400">Billed ✓</p>}
                          {isNext && <p className="text-[10px] text-blue-500 font-medium">Next</p>}
                          {isPaidOff && !isBilled && <p className="text-[10px] text-green-500">Paid off</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {recurring.length === 0 && (
          <div className="bg-white rounded-xl border px-4 py-8 text-center text-gray-400">
            No recurring billing setups yet
          </div>
        )}
      </div>
    </div>
  );
}

const methodLabel = (m) => {
  const map = {
    cash: 'Cash', etransfer: 'E-Transfer', cc: 'Credit Card',
    cheque: 'Cheque', financed: 'Financed', rotessa_auto: 'Rotessa (Auto)',
  };
  return map[m] || m || '—';
};

function HistoryTab() {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const [payments, setPayments] = useState([]);
  const [filterMode, setFilterMode] = useState('month'); // 'month' | 'range' | 'all'
  const [month, setMonth] = useState(currentMonth);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = () => {
    let query = '/payments';
    if (filterMode === 'month' && month)       query += `?month=${month}`;
    else if (filterMode === 'range' && from)   query += `?from=${from}${to ? `&to=${to}` : ''}`;
    api.get(query).then(setPayments);
  };

  useEffect(() => { load(); }, [filterMode, month, from, to]);

  const handleDelete = async (id) => {
    if (!confirm('Delete this payment record?')) return;
    await api.delete(`/payments/${id}`);
    setPayments(prev => prev.filter(p => p.id !== id));
  };

  const shiftMonth = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const total   = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const rotessa = payments.filter(p => p.payment_method === 'rotessa_auto').reduce((s, p) => s + (p.amount || 0), 0);
  const manual  = total - rotessa;

  const byMethod = ['cash','etransfer','cc','cheque'].map(m => ({
    method: m,
    label: methodLabel(m),
    amount: payments.filter(p => p.payment_method === m).reduce((s, p) => s + (p.amount || 0), 0),
  })).filter(m => m.amount > 0);

  return (
    <div>
      {/* Filter bar */}
      <div className="bg-white rounded-xl border p-4 mb-4 flex flex-wrap items-center gap-3">
        {/* Mode selector */}
        <div className="flex rounded-lg border overflow-hidden text-sm">
          {[['month','By Month'],['range','Date Range'],['all','All Time']].map(([m, l]) => (
            <button key={m} onClick={() => setFilterMode(m)}
              className={`px-3 py-1.5 ${filterMode === m ? 'bg-nova-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {l}
            </button>
          ))}
        </div>

        {filterMode === 'month' && (
          <div className="flex items-center gap-2">
            <button onClick={() => shiftMonth(-1)} className="p-1.5 border rounded-lg hover:bg-gray-50">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-nova-500 outline-none" />
            <button onClick={() => shiftMonth(1)} className="p-1.5 border rounded-lg hover:bg-gray-50">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={() => setMonth(currentMonth)}
              className="text-xs px-2 py-1.5 border rounded-lg hover:bg-gray-50 text-gray-500">
              This Month
            </button>
          </div>
        )}

        {filterMode === 'range' && (
          <div className="flex items-center gap-2">
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-nova-500 outline-none" />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-nova-500 outline-none" />
          </div>
        )}

        {filterMode === 'all' && (
          <span className="text-sm text-gray-400">Showing all recorded payments</span>
        )}

        {/* Totals */}
        {payments.length > 0 && (
          <div className="ml-auto flex flex-wrap items-center gap-3 text-sm">
            {byMethod.map(m => (
              <span key={m.method} className={`px-2.5 py-1 rounded-lg font-medium ${
                m.method === 'cash'      ? 'bg-green-50 text-green-700' :
                m.method === 'etransfer' ? 'bg-blue-50 text-blue-700' :
                m.method === 'cc'        ? 'bg-orange-50 text-orange-700' :
                                           'bg-gray-100 text-gray-600'
              }`}>
                {m.label}: {fmt(m.amount)}
              </span>
            ))}
            {rotessa > 0 && (
              <span className="px-2.5 py-1 rounded-lg font-medium bg-purple-50 text-purple-700">
                Rotessa: {fmt(rotessa)}
              </span>
            )}
            <span className="text-green-700 font-bold border-l pl-3">Total: {fmt(total)}</span>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Customer</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Amount</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Method</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Notes</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {payments.map(p => (
              <tr key={p.id} className={`hover:bg-gray-50 ${p.payment_method === 'rotessa_auto' ? 'bg-purple-50/40' : ''}`}>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{p.payment_date}</td>
                <td className="px-4 py-3">
                  <Link to={`/sales/${p.sale_id}`} className="text-nova-600 hover:underline font-medium">{p.customer_name}</Link>
                  <p className="text-xs text-gray-400 capitalize">{p.payment_type}</p>
                </td>
                <td className="px-4 py-3 font-semibold text-green-600 whitespace-nowrap">{fmt(p.amount)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    p.payment_method === 'rotessa_auto' ? 'bg-purple-100 text-purple-700' :
                    p.payment_method === 'cash'         ? 'bg-green-100 text-green-700' :
                    p.payment_method === 'etransfer'    ? 'bg-blue-100 text-blue-700' :
                    p.payment_method === 'cc'           ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{methodLabel(p.payment_method)}</span>
                </td>
                <td className="px-4 py-3 text-gray-400 max-w-xs truncate">{p.notes || '—'}</td>
                <td className="px-4 py-3">
                  {p.payment_method !== 'rotessa_auto' && (
                    <button onClick={() => handleDelete(p.id)} className="text-red-300 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {payments.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                No payments found for this period
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OverdueBadge({ days }) {
  if (days <= 3)  return <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">{days}d overdue</span>;
  if (days <= 7)  return <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">{days}d overdue</span>;
  if (days <= 14) return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">{days}d overdue</span>;
  return <span className="text-xs px-2 py-0.5 rounded-full bg-red-600 text-white font-medium">{days}d overdue</span>;
}

function PastDueTab({ highlightId }) {
  const [pastDue, setPastDue] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const highlightRef = useRef(null);

  const load = () => api.get('/payments/past-due').then(setPastDue);
  useEffect(() => { load(); }, []);

  // Scroll to highlighted row
  useEffect(() => {
    if (highlightId && pastDue.length > 0 && highlightRef.current) {
      setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    }
  }, [highlightId, pastDue]);

  const open = (id, type) => setExpanded(expanded?.id === id && expanded?.type === type ? null : { id, type });

  const handlePayment = async (sale, form) => {
    setSubmitting(true);
    try {
      await api.post('/payments', {
        sale_id: sale.id, amount: Number(form.amount),
        payment_date: form.date, payment_method: form.payment_method, notes: form.notes || '',
      });
      setExpanded(null);
      load();
    } catch (e) { alert(e.message); } finally { setSubmitting(false); }
  };

  const handleRotessaSetup = async (sale, form) => {
    setSubmitting(true);
    try {
      await api.post('/payments/financed-setup', {
        sale_id: sale.id, start_date: form.start_date,
        rotessa_link: form.rotessa_link || null, notes: form.notes || '',
      });
      setExpanded(null);
      load();
    } catch (e) { alert(e.message); } finally { setSubmitting(false); }
  };

  const totalOutstanding = pastDue
    .filter(p => p.payment_type !== 'financed')
    .reduce((sum, p) => sum + (p.remaining || 0), 0);
  const rotessaPending = pastDue.filter(p => p.payment_type === 'financed').length;

  return (
    <div>
      {/* Summary strip */}
      {pastDue.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
            <div>
              <p className="text-xs text-red-500">Overdue since install</p>
              <p className="font-bold text-red-700">{pastDue.length} account{pastDue.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {totalOutstanding > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
              <p className="text-xs text-orange-500">Unpaid balances</p>
              <p className="font-bold text-orange-700">{fmt(totalOutstanding)}</p>
            </div>
          )}
          {rotessaPending > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
              <p className="text-xs text-purple-500">Rotessa PAD not set up</p>
              <p className="font-bold text-purple-700">{rotessaPending} client{rotessaPending !== 1 ? 's' : ''}</p>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Customer</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Install Date</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Issue</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Amount</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Salesman</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {pastDue.map(p => {
              const isFinanced = p.payment_type === 'financed';
              const days = p.days_since_install || 0;
              const isHighlighted = p.id === highlightId;
              return (
                <>
                  <tr
                    key={p.id}
                    ref={isHighlighted ? highlightRef : null}
                    className={`hover:bg-gray-50 ${isHighlighted ? 'ring-2 ring-inset ring-amber-400 bg-amber-50' : days > 14 ? 'bg-red-50' : days > 7 ? 'bg-orange-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <Link to={`/sales/${p.id}`} className="text-nova-600 hover:underline font-medium">{p.customer_name}</Link>
                      <p className="text-xs text-gray-400">{p.city}</p>
                      {p.phone && <p className="text-xs text-gray-400">{p.phone}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{p.scheduled_date}</p>
                      <OverdueBadge days={days} />
                    </td>
                    <td className="px-4 py-3">
                      {isFinanced ? (
                        <div>
                          <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                            <RefreshCw className="w-3 h-3" /> Rotessa not set up
                          </span>
                          <p className="text-xs text-gray-400 mt-1">{fmt(p.finance_monthly_amount)}/mo × {p.finance_term_months}mo</p>
                        </div>
                      ) : (
                        <div>
                          <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                            <DollarSign className="w-3 h-3" /> Payment not received
                          </span>
                          <p className="text-xs text-gray-400 mt-1 capitalize">{p.payment_type}</p>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isFinanced ? (
                        <span className="text-gray-400 text-xs">—</span>
                      ) : (
                        <div>
                          <p className="font-bold text-red-600">{fmt(p.remaining)}</p>
                          <p className="text-xs text-gray-400">of {fmt(p.deal_total)}</p>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{p.salesman_name}</td>
                    <td className="px-4 py-3">
                      {isFinanced ? (
                        <button onClick={() => open(p.id, 'rotessa')}
                          className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 whitespace-nowrap">
                          Setup Rotessa
                        </button>
                      ) : (
                        <button onClick={() => open(p.id, 'payment')}
                          className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 whitespace-nowrap">
                          Mark Received
                        </button>
                      )}
                    </td>
                  </tr>

                  {expanded?.id === p.id && (
                    <tr key={`form-${p.id}`} className={expanded.type === 'rotessa' ? 'bg-purple-50' : 'bg-green-50'}>
                      <td colSpan={6} className="px-6 py-4">
                        {expanded.type === 'payment' && (
                          <PaymentForm sale={p} submitting={submitting}
                            onSave={(form) => handlePayment(p, form)}
                            onCancel={() => setExpanded(null)} />
                        )}
                        {expanded.type === 'rotessa' && (
                          <RotessaSetupForm sale={p} submitting={submitting}
                            onSave={(form) => handleRotessaSetup(p, form)}
                            onCancel={() => setExpanded(null)} />
                        )}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
            {pastDue.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
                All installs are settled — no past due accounts
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Payments() {
  const [searchParams] = useSearchParams();
  const highlightId = Number(searchParams.get('sale_id')) || null;
  const [tab, setTab] = useState(searchParams.get('tab') || 'collections');

  const tabs = [
    { id: 'collections', label: 'Collections', icon: DollarSign },
    { id: 'recurring', label: 'Recurring', icon: RefreshCw },
    { id: 'history', label: 'History', icon: CheckCircle },
    { id: 'past-due', label: 'Past Due', icon: AlertTriangle },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Payments</h1>

      <div className="flex gap-2 mb-4">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
              tab === t.id
                ? t.id === 'past-due' ? 'bg-red-600 text-white'
                  : t.id === 'recurring' ? 'bg-purple-600 text-white'
                  : 'bg-nova-600 text-white'
                : 'bg-white border text-gray-600'
            }`}>
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {highlightId && (
        <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
          Highlighted: showing record for this customer — <button onClick={() => setTab(tab)} className="underline">scroll to see it</button>
        </div>
      )}

      {tab === 'collections' && <CollectionsTab highlightId={highlightId} />}
      {tab === 'recurring' && <RecurringTab highlightId={highlightId} />}
      {tab === 'history' && <HistoryTab />}
      {tab === 'past-due' && <PastDueTab highlightId={highlightId} />}
    </div>
  );
}
